import type { TFunction } from "i18next";

import type { components } from "@/api/schema";

import { humanize } from "./labels";
import { targetToHref } from "./target-link";

type GlobalAuditEvent = components["schemas"]["GlobalAuditEvent"];

export interface EventDetail {
  label: string;
  value: string;
  /** Present only for the metadata keys that also resolve to an in-app route. */
  href?: string;
}

export interface EventDescription {
  /** Outcome-first headline, sentence case (i18n `activity.<code>.title`). */
  title: string;
  /** One or two past-tense sentences. Paragraphs are separated by a blank line. */
  summary: string;
  /** One short line for the table (i18n `activity.<code>.brief`); falls back
   * to the summary's first sentence. The target column already names the
   * object, so a brief carries only what the title does not. */
  brief: string;
  /** Numbered remediation steps — `null` unless the event failed AND copy exists. */
  fixSteps: string[] | null;
  details: EventDetail[];
  links: { order?: string; sku?: string };
}

/** Never shown to an operator: plumbing (`idempotency_key`, `severity`) or a
 * machine code the prose already says in words (`error_code`). */
const INTERNAL_METADATA_KEYS = new Set(["idempotency_key", "severity", "error_code"]);

/** Marketplace codes are lowercase slugs in metadata; these are the brand
 * spellings used everywhere else in the UI. Unknown slugs get title case. */
const MARKETPLACE_LABELS: Record<string, string> = {
  shopee: "Shopee",
  lazada: "Lazada",
  tiktok: "TikTok",
};

/** UX writing rule 4: numbers carry thousands separators (1,373). Fixed
 * locale so the rendering is deterministic across environments, same as the
 * date formatters in AuditTable. */
const numberFormatter = new Intl.NumberFormat("en-US");

/** Generic `promotions.<action>_completed` bulk-result template (canonical.go
 * mints one per bulk action, so they can't all be enumerated in the catalog). */
const PROMOTIONS_COMPLETED = /^promotions\.(.+)_completed$/;

const MAX_FIX_STEPS = 10;

/**
 * Turns one audit event into the operator-facing copy the table and the
 * drawer both render: an outcome title, a plain-language summary, optional
 * "to fix this" steps, the non-internal metadata as label/value rows, and the
 * order/SKU routes the event points at.
 *
 * Pure: every string comes from `t` (the "audit" namespace) and the event
 * itself. Copy is selected by activity code, optionally refined by a variant
 * (`metadata.reason`, or changed/unchanged for `metadata.changed`), so a
 * reason-specific sentence wins over the activity's generic one.
 */
export function describeEvent(t: TFunction, event: GlobalAuditEvent): EventDescription {
  const code = event.activity;
  const metadata = event.metadata ?? {};
  const changes = event.changes ?? {};
  const variant = variantOf(metadata);

  const { order: orderId, sku } = eventRelatedTo(event);
  const links = buildLinks(code, orderId, sku);
  const vars = buildVars(t, event, metadata, changes, orderId, sku);

  const generic = PROMOTIONS_COMPLETED.exec(code);
  const genericVars = generic ? { ...vars, action: humanize(generic[1] as string) } : vars;

  const title =
    resolve(t, code, variant, "title", vars) ||
    (generic ? t("audit:activity.promotions._completed.title", { ...genericVars }) : "") ||
    humanize(code);
  const summary =
    resolve(t, code, variant, "summary", vars) ||
    (generic ? t("audit:activity.promotions._completed.summary", { ...genericVars }) : "") ||
    t("audit:activity.unknown.summary", { ...vars });

  const brief = resolve(t, code, variant, "brief", vars) || firstSentence(summary);

  return {
    title,
    summary,
    brief,
    fixSteps: buildFixSteps(t, code, variant, event.result, vars),
    details: buildDetails(t, metadata, links),
    links,
  };
}

function firstSentence(text: string): string {
  const match = /^(.+?[.!?])(\s|$)/.exec(text);
  return match ? (match[1] as string) : text;
}

/**
 * Which reason-specific copy variant applies. `metadata.reason` is the stable
 * code the backend emits for failures (`stock_non_negative`,
 * `illegal_transition`, …) and for review flags (`oversold`, …). A boolean
 * `metadata.changed` (order.refreshed) selects changed/unchanged copy instead
 * of an i18next plural, which cannot express two different sentences.
 */
function variantOf(metadata: Record<string, unknown>): string | undefined {
  const reason = asString(metadata.reason);
  if (reason !== undefined) return reason;
  if (typeof metadata.changed === "boolean") return metadata.changed ? "changed" : "unchanged";
  return undefined;
}

/**
 * Looks up `activity.<code>.<variant>.<kind>` and falls back to
 * `activity.<code>.<kind>`. `defaultValue: ""` is the pure-function stand-in
 * for `i18n.exists` — `describeEvent` only receives `t`, not the i18n
 * instance, and an empty string means "no copy authored for this".
 */
function resolve(
  t: TFunction,
  code: string,
  variant: string | undefined,
  kind: string,
  vars: Record<string, unknown>,
): string {
  if (variant !== undefined) {
    const specific = t(`audit:activity.${code}.${variant}.${kind}`, { ...vars, defaultValue: "" });
    if (specific) return specific;
  }
  return t(`audit:activity.${code}.${kind}`, { ...vars, defaultValue: "" });
}

/**
 * UX writing rule 5: remediation steps only for a failed/partial result AND
 * only when real copy exists — never a placeholder. The variant's step list
 * is taken whole or not at all, so variant and generic steps never interleave.
 */
function buildFixSteps(
  t: TFunction,
  code: string,
  variant: string | undefined,
  result: string,
  vars: Record<string, unknown>,
): string[] | null {
  if (result !== "failed" && result !== "partial") return null;

  const variantPrefix = `audit:activity.${code}.${variant}.fix.`;
  const prefix =
    variant !== undefined && t(`${variantPrefix}1`, { ...vars, defaultValue: "" })
      ? variantPrefix
      : `audit:activity.${code}.fix.`;

  const steps: string[] = [];
  for (let i = 1; i <= MAX_FIX_STEPS; i += 1) {
    const step = t(`${prefix}${i}`, { ...vars, defaultValue: "" });
    if (!step) break;
    steps.push(step);
  }
  return steps.length > 0 ? steps : null;
}

/** Metadata keys whose values are enum codes: labelled from i18n `value.<key>.<code>`;
 * the status-like ones fall back to a humanized code (READY_TO_SHIP → "Ready to ship")
 * so a status the catalogue does not know still reads as words. */
const CODED_KEYS = new Set([
  "reason",
  "result",
  "mode",
  "choice",
  "cancel_reason",
  "marketplace_result",
  "lifecycle",
  "status",
  "paused_reason",
]);
const STATUS_LIKE_KEYS = new Set([
  "attempted_status",
  "channel_state",
  "raw_status",
  "marketplace_status",
  "lifecycle",
  "status",
]);

/** Plain-words label for a `changes` field name (i18n `field.<name>`), humanized when unknown. */
export function fieldLabel(t: TFunction, field: string): string {
  return t(`audit:field.${field}`, { defaultValue: "" }) || humanize(field);
}

/** Plain-words label for a target type (i18n `target_type.<code>`), humanized when unknown. */
export function targetTypeLabel(t: TFunction, targetType: string): string {
  return t(`audit:target_type.${targetType}`, { defaultValue: "" }) || humanize(targetType);
}

/** A stock_push target reads "SKU 000013 · Shopee" instead of "000013·shopee". */
export function targetLabel(
  event: Pick<GlobalAuditEvent, "target_type" | "target_id" | "target_label">,
): string {
  const { sku, marketplace } = stockPushTarget(event);
  if (marketplace === undefined) return event.target_label;
  const brand = marketplaceLabel(marketplace);
  return sku === undefined ? brand : `SKU ${sku} · ${brand}`;
}

/** Plain-words label for a coded value; `marketplace_result` shares the `result` catalogue. */
export function valueLabel(t: TFunction, key: string, value: unknown): string {
  const raw = formatValue(value);
  if (typeof value !== "string" || value === "") return raw;
  if (key === "marketplace") return marketplaceLabel(value);
  const catalogue = key === "marketplace_result" ? "result" : key;
  if (CODED_KEYS.has(key)) {
    const label = t(`audit:value.${catalogue}.${value}`, { defaultValue: "" });
    if (label) return label;
  }
  if (STATUS_LIKE_KEYS.has(key)) return humanize(value);
  return raw;
}

function buildDetails(
  t: TFunction,
  metadata: Record<string, unknown>,
  links: EventDescription["links"],
): EventDetail[] {
  return Object.entries(metadata)
    .filter(([key]) => !INTERNAL_METADATA_KEYS.has(key))
    .map(([key, value]) => {
      const href = key === "order_id" ? links.order : key === "sku" ? links.sku : undefined;
      return {
        label: t(`audit:detail.${key}`, { defaultValue: "" }) || humanize(key),
        value: valueLabel(t, key, value),
        ...(href !== undefined && { href }),
      };
    });
}

/**
 * The order / SKU this event is *about*, whether it says so through its target
 * or through metadata (an inventory movement targets the SKU but names the
 * order). These are the raw ids behind both the drawer's links and the
 * `related_to=order:<id>` / `sku:<id>` timeline filter.
 */
export function eventRelatedTo(
  event: Pick<GlobalAuditEvent, "target_type" | "target_id" | "metadata">,
): { order?: string; sku?: string } {
  const metadata = event.metadata ?? {};
  const sku =
    asString(metadata.sku) ??
    (event.target_type === "sku" ? event.target_id : undefined) ??
    stockPushTarget(event).sku;
  const order =
    asString(metadata.order_id) ?? (event.target_type === "order" ? event.target_id : undefined);
  return {
    ...(order !== undefined && { order }),
    ...(sku !== undefined && { sku }),
  };
}

/**
 * `stock_push` targets are written as "<sku>·<marketplace>" for a per-row
 * push (stock.manual_sync) or just "<marketplace>" for a marketplace-wide
 * event (push paused/resumed/mode) — the only place the SKU and marketplace
 * live for those activities, since their metadata does not repeat them.
 */
function stockPushTarget(event: Pick<GlobalAuditEvent, "target_type" | "target_id">): {
  sku?: string;
  marketplace?: string;
} {
  if (event.target_type !== "stock_push" || !event.target_id) return {};
  const [head, tail] = event.target_id.split("·");
  if (!head) return {};
  if (tail !== undefined) return { sku: head, marketplace: tail };
  return { marketplace: head };
}

/** Reuses `targetToHref` so audit links can never drift from the table's. */
function buildLinks(
  activity: string,
  orderId: string | undefined,
  sku: string | undefined,
): EventDescription["links"] {
  const order =
    orderId !== undefined ? targetToHref({ target_type: "order", target_id: orderId }) : null;
  const skuHref =
    sku !== undefined ? targetToHref({ target_type: "sku", target_id: sku, activity }) : null;
  return {
    ...(order !== null && { order }),
    ...(skuHref !== null && { sku: skuHref }),
  };
}

/**
 * Interpolation variables available to every `activity.*` string:
 * `target`/`actor`, a capitalized `marketplace`, resolved `order_id`/`sku`,
 * every metadata key, every changes field as `{{field.old}}`/`{{field.new}}`,
 * and the first changed field's `{{old}}`/`{{new}}`/`{{delta}}`.
 */
function buildVars(
  t: TFunction,
  event: GlobalAuditEvent,
  metadata: Record<string, unknown>,
  changes: GlobalAuditEvent["changes"],
  orderId: string | undefined,
  sku: string | undefined,
): Record<string, unknown> {
  const vars: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(metadata)) {
    vars[key] = valueLabel(t, key, value);
  }
  for (const [field, change] of Object.entries(changes)) {
    vars[field] = { old: valueLabel(t, field, change.old), new: valueLabel(t, field, change.new) };
  }

  const [firstField, first] = Object.entries(changes)[0] ?? [];
  if (first !== undefined && firstField !== undefined) {
    vars.old = valueLabel(t, firstField, first.old);
    vars.new = valueLabel(t, firstField, first.new);
    if (typeof first.old === "number" && typeof first.new === "number") {
      vars.delta = numberFormatter.format(Math.abs(first.new - first.old));
    }
  }

  vars.target = event.target_label;
  vars.actor = event.actor_display_name;
  // Falls back to a generic noun so copy reads as a sentence for the
  // activities whose metadata carries no marketplace (order.cancel_*).
  vars.marketplace = marketplaceLabel(
    asString(metadata.marketplace) ?? stockPushTarget(event).marketplace,
  );
  if (orderId !== undefined) vars.order_id = orderId;
  if (sku !== undefined) vars.sku = sku;

  return vars;
}

export function marketplaceLabel(code: string | undefined): string {
  if (code === undefined || code === "") return "the marketplace";
  return MARKETPLACE_LABELS[code.toLowerCase()] ?? humanize(code);
}

/**
 * Renders a metadata/changes value as display text. Numbers get thousands
 * separators (UX writing rule 4); arrays read as a list rather than JSON;
 * anything else falls back to JSON so nothing ever renders "[object Object]".
 */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number")
    return Number.isFinite(value) ? numberFormatter.format(value) : String(value);
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  return JSON.stringify(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}
